import { buildHttpResponse, buildFormDataRequest, HttpMethod } from "./network_utility";
import { ChartRecord, ReChartData } from "../models/chart_model";
import { ApiResponse } from "../models/api_res";
import {
  getFiscalYear,
  GetNumberOfDaysInMonth,
  getQuarter,
} from "../extensions/date";
import { getLoggedIn } from "../pages/Protected";
import { ResetPasswordResponse, SendOtpResponse, VerifyOtpResponse } from "../types/api";
import { ConsumptionData } from "../models/consumption";
import { GenerationOutageData } from "../models/generationOutage";
import { GenerationData } from "../models/generationData";
import { ExchangeBidData } from "../models/exchangeBidData";
import { ElectricityParData } from "../models/parData";
import { FilterExchangeData } from "../pages/home/fill_missing";

export function encryptPassword(text: string, key = "SIMPLE_KEY"): string {
  return btoa(
    text.split('').map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length))
    ).join('')
);
}



export async function login(username: string, password: string): Promise<ApiResponse> {
  // Encrypt the password
  const encryptedPassword = encryptPassword(password); // Encrypt password

  const response = await buildHttpResponse({
    endPoint: "gas/login/",
    method: HttpMethod.POST,
    body: {
      username: username,
      password: encryptedPassword // Send encrypted password
    },
  });

  return response as ApiResponse;
}


export async function getOtp(email: string): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: "gas/requestOtp/",
    method: HttpMethod.POST,
    body: {
      email:email
    },
  });
  return response as ApiResponse;
}

export async function registerUser(
  data: any
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: "gas/register/",
    method: HttpMethod.POST,
    body: data
  });
  return response as ApiResponse;
}


export async function checkAccess(accessKey: string): Promise<ApiResponse> {
  return {"status":await getLoggedIn(),"message": "Logged In" };
}



export async function verifyToken(): Promise<{status: boolean}> {
  const response = await buildHttpResponse({
    endPoint: "gas/verifyToken/",
    method: HttpMethod.GET,
  });
  return response;
}

export async function sendResetOtp(email: string): Promise<SendOtpResponse> {
  const response = await buildHttpResponse({
    endPoint: "gas/send-reset-otp/",
    method: HttpMethod.POST,
    body: { email }
  });
  return response as SendOtpResponse;
}

export async function verifyResetOtp(otp: string, otpId: string): Promise<VerifyOtpResponse> {
  const response = await buildHttpResponse({
    endPoint: "gas/verify-reset-otp/",
    method: HttpMethod.POST,
    body: { otp, otpId }
  });
  return response as VerifyOtpResponse;
}

export async function resetPassword(resetToken: string, newPassword: string): Promise<ResetPasswordResponse> {
  const response = await buildHttpResponse({
    endPoint: "gas/reset-password/",
    method: HttpMethod.POST,
    body: { resetToken, newPassword }
  });
  return response as ResetPasswordResponse;
}


export async function getCollectiveData(startDate: string, endDate: string): Promise<ExchangeBidData[]> {
  const response = await buildHttpResponse({
    endPoint: `data/getData/?start_date=${startDate}&end_date=${endDate}`,
    method: HttpMethod.GET,
  });
  return response.data.map((item: any) => ({
    ...item,
    wt_mcp_rs_mwh: parseFloat(item.wt_mcp_rs_mwh),
    purchase_bid_mwh: parseFloat(item.purchase_bid_mw)/4,
    sell_bid_mwh: parseFloat(item.sell_bid_mw)/4,
    mcv_mwh: parseFloat(item.mcv_mw)/4,
  }));
}
export const formatDateToDDMMYYYY = (date: Date | string): string => {
  const dateObj = new Date(date);
  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}-${month}-${year}`;
};


export async function getConsumptionData(startDate: string, endDate: string): Promise<ConsumptionData[]> {
  const response = await buildHttpResponse({
    endPoint: "data/consumption_api",
    method: HttpMethod.POST,
    body: {
      start_date: formatDateToDDMMYYYY(startDate),
      end_date: formatDateToDDMMYYYY(endDate),
    },
  });
  const data= response.map((item: any) => ({
    ...item,
    date:(item.date),
    energy_met_mu: parseFloat(item.energy_met_mu),
    states: item.states,
  }));
  console.log("Consumption Data = ", data);
  return data;
}

export async function getGenerationOutageData(startDate: string, endDate: string): Promise<GenerationOutageData[]> {
  const response = await buildFormDataRequest({
    endpoint: "data/generation_outage_api",
    method: "POST",
    body: {
      start_date: formatDateToDDMMYYYY(startDate),
      end_date: formatDateToDDMMYYYY(endDate),
    },
  });
  return response;
}

export async function getGenerationData(startDate: string, endDate: string): Promise<GenerationData[]> {
  const response = await buildFormDataRequest({
    endpoint: "data/generation_data_api",
    method: "POST",
    body: {
      start_date: formatDateToDDMMYYYY(startDate),
      end_date: formatDateToDDMMYYYY(endDate),
    },
  });
  return response.map((item: any) => ({
    ...item,
    date: item.date,
    generation_mu_actual: parseFloat(item.generation_mu_actual),
    states: item.states,
  }));
}
export async function getElectricityParData(startDate: string, endDate: string): Promise<ElectricityParData[]> {
  // Helper function to create base ElectricityParData object
  const createBaseData = (date: string): ElectricityParData => ({
    date,
    generation_million_mwh: 0,
    consumption_million_mwh: 0,
    outage_million_mwh: 0,
    purchase_bid_million_mwh: 0,
    sell_bid_million_mwh: 0,
    mcv_million_mwh: 0,
    wt_mcp_rs_mwh: 0,
  });

  // Helper function to transform generation data
  const transformGenerationData = (item: any): ElectricityParData => ({
    ...createBaseData(item.date),
    generation_million_mwh: parseFloat(item.generation_mu_actual) / 1000,
  });

  // Helper function to transform consumption data
  const transformConsumptionData = (item: any): ElectricityParData => ({
    ...createBaseData(item.date),
    consumption_million_mwh: parseFloat(item.energy_met_mu) / 1000,
  });

  // Helper function to transform outage data
  const transformOutageData = (item: any): ElectricityParData => ({
    ...createBaseData(item.date),
    outage_million_mwh: parseFloat(item.total) / 1000000,
  });

  // Helper function to transform exchange data
  const transformExchangeData = (item: any): ElectricityParData => ({
    ...createBaseData(formatDateToDDMMYYYY(item.date)),
    purchase_bid_million_mwh: parseFloat(item.purchase_bid_mwh) / 1000000,
    sell_bid_million_mwh: parseFloat(item.sell_bid_mwh) / 1000000,
    mcv_million_mwh: parseFloat(item.mcv_mwh) / 1000000,
    wt_mcp_rs_mwh: parseFloat(item.wt_mcp_rs_mwh),
  });

  try {
    // Execute all API calls in parallel for better performance
    const [generationData, consumptionData, outageData, exchangeData] = await Promise.all([
      getGenerationData(startDate, endDate),
      getConsumptionData(startDate, endDate),
      getGenerationOutageData(startDate, endDate),
      getCollectiveData(startDate, endDate),
    ]);

    // Transform all data in parallel
    const [gen_data, consumption_data, outage_data, exchange_data] = await Promise.all([
      Promise.resolve(generationData.map(transformGenerationData)),
      Promise.resolve(consumptionData.map(transformConsumptionData)),
      Promise.resolve(outageData.map(transformOutageData)),
      Promise.resolve(FilterExchangeData(exchangeData,{exchangeIndex: 0, exchangeProductIndex: 0}).map(transformExchangeData)),
    ]);

    // Combine all data
    const joined_data = [...gen_data, ...consumption_data, ...outage_data, ...exchange_data];
    
    console.log("Joined Data = ", joined_data);
    return joined_data as ElectricityParData[];
  } catch (error) {
    console.error("Error fetching electricity PAR data:", error);
    throw error;
  }
}


export async function getBuyerSellersData(formData: FormData): Promise<any> {
  const response = await buildHttpResponse({
    endPoint: `data/getTopBuyerSeller/`,
    method: HttpMethod.POST,
    body: formData,
  });
  return response;
}

